import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { useAuthStore } from '../store/authStore';
import { clearToken } from '../utils/tokenStorage';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CheckCircle, XCircle, Calendar, Star, Users, MessageCircle, Inbox, Megaphone, Video, Radio, Crown, Palette, Plus, ScanLine, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';
import ChatWindow from '../components/ChatWindow';
import AdminLayout from '../components/AdminLayout';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const [pendingArtists, setPendingArtists] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('artists');
  const [conversations, setConversations] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [featuredDJ, setFeaturedDJ] = useState(null);
  const [approvedArtists, setApprovedArtists] = useState([]);
  const [featuredNote, setFeaturedNote] = useState('');
  const [availableThemes, setAvailableThemes] = useState({});
  const [currentTheme, setCurrentTheme] = useState('default');
  const [events, setEvents] = useState([]);
  const [showEventForm, setShowEventForm] = useState(false);
  const [newEvent, setNewEvent] = useState({ title: '', description: '', event_date: '', event_time: '20:00', location: 'RazientSounds Virtual Nightclub' });

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin' && user.role !== 'owner' && user.role !== 'overseer') {
      navigate('/dashboard');
      return;
    }
    fetchData();
    // poll booking chats so new inquiries ping the team with an unread count
    const iv = setInterval(async () => {
      try {
        const r = await apiClient.get('/admin/booking-chats');
        setConversations(r.data || []);
      } catch (e) { /* ignore */ }
    }, 30000);
    return () => clearInterval(iv);
  }, [user]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [artistsRes, bookingsRes, featuredRes, approvedRes, themesRes, themeRes, eventsRes] = await Promise.all([
        apiClient.get('/admin/artists/pending'),
        apiClient.get('/admin/bookings'),
        apiClient.get('/featured-dj').catch(() => ({ data: { featured: null } })),
        apiClient.get('/admin/approved-artists').catch(() => ({ data: [] })),
        apiClient.get('/nightclub/themes').catch(() => ({ data: {} })),
        apiClient.get('/nightclub/current-theme').catch(() => ({ data: { theme_id: 'default' } })),
        apiClient.get('/events').catch(() => ({ data: [] }))
      ]);
      setPendingArtists(artistsRes.data);
      setAllBookings(Array.isArray(bookingsRes.data) ? bookingsRes.data : []);
      setFeaturedDJ(featuredRes.data.featured);
      setApprovedArtists(approvedRes.data);
      setAvailableThemes(themesRes.data);
      setCurrentTheme(themeRes.data.theme_id || 'default');
      setEvents(Array.isArray(eventsRes.data) ? eventsRes.data : []);
      if (featuredRes.data.featured) {
        setFeaturedNote(featuredRes.data.featured.featured_note || '');
      }
      
      // All booking conversations (incl. Book-Through-Us inquiries) — team-managed
      const chatsRes = await apiClient.get('/admin/booking-chats');
      setConversations(chatsRes.data || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleArtistStatus = async (artistId, status) => {
    try {
      await apiClient.put(`/admin/artists/${artistId}/approve`, { status });
      toast.success(`Artist ${status} successfully!`);
      fetchData();
    } catch (error) {
      console.error('Error updating artist status:', error);
      toast.error('Failed to update artist status');
    }
  };

  const handleSetFeaturedDJ = async (artistId) => {
    try {
      await apiClient.put('/admin/featured-dj', { artist_id: artistId, note: featuredNote });
      toast.success(artistId ? 'Featured DJ updated!' : 'Featured DJ cleared!');
      fetchData();
    } catch (error) {
      toast.error('Failed to update featured DJ');
    }
  };

  const handleSetTheme = async (themeId) => {
    try {
      await apiClient.put('/admin/nightclub/theme', { theme_id: themeId });
      setCurrentTheme(themeId);
      toast.success(`Theme changed to ${availableThemes[themeId]?.name || themeId}!`);
    } catch (error) {
      toast.error('Failed to change theme');
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.event_date) {
      toast.error('Title and date are required');
      return;
    }
    try {
      await apiClient.post('/events', { ...newEvent, is_public: true });
      toast.success('Event created!');
      setShowEventForm(false);
      setNewEvent({ title: '', description: '', event_date: '', event_time: '20:00', location: 'RazientSounds Virtual Nightclub' });
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to create event');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      await apiClient.delete(`/events/${eventId}`);
      toast.success('Event deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handleBookingStatus = async (bookingId, status) => {
    try {
      await apiClient.put(`/admin/bookings/${bookingId}/status?status=${status}`);
      toast.success(`Booking ${status} successfully!`);
      fetchData();
    } catch (error) {
      console.error('Error updating booking status:', error);
      toast.error('Failed to update booking status');
    }
  };

  const handleLogout = async () => {
    try {
      await apiClient.post('/auth/logout');
      clearToken();
      logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold">Dashboard</h1>
          <p className="text-white/60">Welcome back, {user?.name}</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="glass-default border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm mb-1">Pending Artists</p>
                  <p className="text-3xl font-bold">{pendingArtists.length}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Users className="text-yellow-400" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-default border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm mb-1">Total Bookings</p>
                  <p className="text-3xl font-bold">{allBookings.length}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Calendar className="text-blue-400" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="glass-default border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white/60 text-sm mb-1">Pending Bookings</p>
                  <p className="text-3xl font-bold">
                    {allBookings.filter(b => b.booking_status === 'pending').length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center">
                  <Star className="text-violet-400" size={24} />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions - Announcements & Go Live */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <Card 
            className="glass-default border-white/10 cursor-pointer hover:border-violet-500/50 transition-all"
            onClick={() => navigate('/admin/announcements')}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 flex items-center justify-center">
                  <Megaphone className="text-white" size={28} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white">Announcements</h3>
                  <p className="text-sm text-white/60">Set daily messages for all visitors</p>
                </div>
                <Button 
                  onClick={(e) => { e.stopPropagation(); navigate('/admin/announcements'); }}
                  className="bg-violet-600 hover:bg-violet-500"
                >
                  Manage
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card 
            className="glass-default border-white/10 cursor-pointer hover:border-red-500/50 transition-all"
            onClick={() => navigate('/admin/announcements')}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-red-600 to-pink-600 flex items-center justify-center">
                  <Radio className="text-white" size={28} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white">Go Live</h3>
                  <p className="text-sm text-white/60">Stream live to all visitors</p>
                </div>
                <Button 
                  onClick={(e) => { e.stopPropagation(); navigate('/admin/announcements'); }}
                  className="bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500"
                >
                  <Radio size={16} className="mr-2" />
                  Go Live
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card
            className="glass-default border-white/10 cursor-pointer hover:border-purple-500/50 transition-all"
            onClick={() => navigate('/admin/tickets/scan')}
            data-testid="admin-scanner-card"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-600 to-fuchsia-600 flex items-center justify-center">
                  <ScanLine className="text-white" size={28} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white">Ticket Scanner</h3>
                  <p className="text-sm text-white/60">Scan QR codes at the door</p>
                </div>
                <Button
                  onClick={(e) => { e.stopPropagation(); navigate('/admin/tickets/scan'); }}
                  className="bg-purple-600 hover:bg-purple-500"
                >
                  Open
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card
            className="glass-default border-white/10 cursor-pointer hover:border-green-500/50 transition-all"
            onClick={() => navigate('/admin/events/stats')}
            data-testid="admin-attendance-card"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-600 to-emerald-600 flex items-center justify-center">
                  <BarChart3 className="text-white" size={28} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white">Event Attendance</h3>
                  <p className="text-sm text-white/60">Tickets issued vs scanned per event</p>
                </div>
                <Button
                  onClick={(e) => { e.stopPropagation(); navigate('/admin/events/stats'); }}
                  className="bg-green-600 hover:bg-green-500"
                >
                  View
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Featured DJ Card */}
          <Card className="glass-default border-white/10 hover:border-yellow-500/50 transition-all">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-600 to-amber-600 flex items-center justify-center">
                  <Crown className="text-white" size={28} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white">Featured DJ of the Week</h3>
                  <p className="text-sm text-white/60">
                    {featuredDJ ? `Current: ${featuredDJ.user_name || featuredDJ.display_name || 'Unknown'}` : 'No featured DJ set'}
                  </p>
                </div>
              </div>
              <div className="mt-4 space-y-3">
                <select
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                  data-testid="featured-dj-select"
                  value={featuredDJ?.artist_id || ''}
                  onChange={(e) => handleSetFeaturedDJ(e.target.value || null)}
                >
                  <option value="">-- No Featured DJ --</option>
                  {approvedArtists.map((a) => (
                    <option key={a.artist_id} value={a.artist_id}>
                      {a.display_name} {a.genres?.length > 0 ? `(${a.genres.slice(0, 2).join(', ')})` : ''}
                    </option>
                  ))}
                </select>
                <Input
                  placeholder="Spotlight note (e.g. 'Killing it this week!')"
                  value={featuredNote}
                  onChange={(e) => setFeaturedNote(e.target.value)}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="featured-dj-note"
                />
                {featuredNote !== (featuredDJ?.featured_note || '') && featuredDJ && (
                  <Button
                    size="sm"
                    onClick={() => handleSetFeaturedDJ(featuredDJ.artist_id)}
                    className="bg-yellow-600 hover:bg-yellow-500"
                    data-testid="update-featured-note-btn"
                  >
                    Update Note
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Themed Nights Card */}
        <Card className="glass-default border-white/10 mb-8" data-testid="themed-nights-admin">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 flex items-center justify-center">
                <Palette className="text-white" size={28} />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-white">Themed Nights</h3>
                <p className="text-sm text-white/60">
                  Current: {availableThemes[currentTheme]?.name || 'Classic Night'}
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(availableThemes).map(([id, theme]) => (
                <button
                  key={id}
                  onClick={() => handleSetTheme(id)}
                  className={`p-3 rounded-xl border-2 transition-all text-left ${
                    currentTheme === id
                      ? 'border-violet-500 bg-violet-500/20'
                      : 'border-white/10 hover:border-white/30 bg-white/5'
                  }`}
                  data-testid={`theme-${id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ background: theme.accentColor }}
                    />
                    <span className="text-sm font-medium text-white">{theme.name}</span>
                  </div>
                  <p className="text-xs text-white/40">{theme.genre}</p>
                  <p className="text-[10px] text-white/30 mt-1">{theme.description}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Event Calendar Management */}
        <Card className="glass-default border-white/10 mb-8" data-testid="events-admin">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-600 to-cyan-600 flex items-center justify-center">
                  <Calendar className="text-white" size={28} />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Event Calendar</h3>
                  <p className="text-sm text-white/60">{events.length} events</p>
                </div>
              </div>
              <Button
                onClick={() => setShowEventForm(!showEventForm)}
                className="bg-blue-600 hover:bg-blue-500"
                data-testid="create-event-btn"
              >
                <Plus size={16} className="mr-1" /> New Event
              </Button>
            </div>

            {showEventForm && (
              <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10 space-y-3" data-testid="event-form">
                <Input
                  placeholder="Event Title"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, title: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="event-title-input"
                />
                <Textarea
                  placeholder="Description"
                  value={newEvent.description}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, description: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="event-description-input"
                />
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    type="date"
                    value={newEvent.event_date}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, event_date: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="event-date-input"
                  />
                  <Input
                    type="time"
                    value={newEvent.event_time}
                    onChange={(e) => setNewEvent(prev => ({ ...prev, event_time: e.target.value }))}
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="event-time-input"
                  />
                </div>
                <Input
                  placeholder="Location"
                  value={newEvent.location}
                  onChange={(e) => setNewEvent(prev => ({ ...prev, location: e.target.value }))}
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="event-location-input"
                />
                <div className="flex gap-2">
                  <Button onClick={handleCreateEvent} className="bg-green-600 hover:bg-green-500" data-testid="submit-event-btn">
                    <CheckCircle size={16} className="mr-1" /> Create Event
                  </Button>
                  <Button variant="ghost" onClick={() => setShowEventForm(false)} className="text-white/60">Cancel</Button>
                </div>
              </div>
            )}

            {events.length === 0 ? (
              <p className="text-white/40 text-sm text-center py-4">No events yet. Create one above!</p>
            ) : (
              <div className="space-y-3 max-h-[300px] overflow-y-auto">
                {events.map(event => (
                  <div key={event.event_id} className="flex items-center justify-between p-3 rounded-lg bg-white/5" data-testid={`event-${event.event_id}`}>
                    <div>
                      <p className="text-white font-medium text-sm">{event.title}</p>
                      <p className="text-white/40 text-xs">
                        {event.event_date} at {event.event_time} &bull; {event.attendees?.length || 0} attending
                      </p>
                    </div>
                    <Button
                      variant="ghost" size="sm"
                      className="text-red-400 hover:bg-red-500/20"
                      onClick={() => handleDeleteEvent(event.event_id)}
                      data-testid={`delete-event-${event.event_id}`}
                    >
                      <XCircle size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="glass-default border border-white/10 p-1 flex flex-wrap">
            <TabsTrigger value="artists" className="data-[state=active]:bg-violet-600 text-xs sm:text-sm">
              <Users size={16} className="mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Pending </span>Artists ({pendingArtists.length})
            </TabsTrigger>
            <TabsTrigger value="bookings" className="data-[state=active]:bg-violet-600 text-xs sm:text-sm">
              <Calendar size={16} className="mr-1 sm:mr-2" />
              Bookings ({allBookings.length})
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-violet-600 text-xs sm:text-sm relative">
              <MessageCircle size={16} className="mr-1 sm:mr-2" />
              Messages ({conversations.length})
              {conversations.reduce((s, c) => s + (c.team_unread || 0), 0) > 0 && (
                <span className="ml-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="admin-unread-badge">
                  {conversations.reduce((s, c) => s + (c.team_unread || 0), 0)}
                </span>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Pending Artists Tab */}
          <TabsContent value="artists" className="space-y-4">
            {pendingArtists.length === 0 ? (
              <Card className="glass-default border-white/10">
                <CardContent className="p-12 text-center">
                  <CheckCircle size={48} className="mx-auto mb-4 text-green-400" />
                  <p className="text-xl text-white/60">No pending artist approvals</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-6">
                {pendingArtists.map((artist) => (
                  <Card
                    key={artist.artist_id}
                    data-testid={`pending-artist-${artist.artist_id}`}
                    className="glass-default border-white/10"
                  >
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>Artist Application</span>
                        <span className="text-sm font-normal px-3 py-1 rounded-full bg-yellow-500/20 text-yellow-300">
                          Pending Review
                        </span>
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm text-white/60 mb-1">Bio</p>
                            <p className="text-white">{artist.bio}</p>
                          </div>
                          <div>
                            <p className="text-sm text-white/60 mb-1">Genres</p>
                            <div className="flex flex-wrap gap-2">
                              {artist.genres.map((genre, idx) => (
                                <span key={idx} className="px-2 py-1 rounded-full bg-violet-500/20 text-violet-300 text-sm">
                                  {genre}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="space-y-3">
                          <div>
                            <p className="text-sm text-white/60 mb-1">Type</p>
                            <p className="text-white">{artist.is_local ? 'Local Artist' : 'Available for Travel'}</p>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-3 pt-4 border-t border-white/10">
                        <Button
                          data-testid={`approve-artist-${artist.artist_id}`}
                          onClick={() => handleArtistStatus(artist.artist_id, 'approved')}
                          className="flex-1 bg-green-600 hover:bg-green-500"
                        >
                          <CheckCircle className="mr-2" size={18} />
                          Approve Artist
                        </Button>
                        <Button
                          data-testid={`reject-artist-${artist.artist_id}`}
                          onClick={() => handleArtistStatus(artist.artist_id, 'rejected')}
                          variant="outline"
                          className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <XCircle className="mr-2" size={18} />
                          Reject
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* All Bookings Tab */}
          <TabsContent value="bookings" className="space-y-4">
            {allBookings.length === 0 ? (
              <Card className="glass-default border-white/10">
                <CardContent className="p-12 text-center">
                  <Calendar size={48} className="mx-auto mb-4 text-white/40" />
                  <p className="text-xl text-white/60">No bookings yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {allBookings.map((booking) => (
                  <Card
                    key={booking.booking_id}
                    data-testid={`booking-${booking.booking_id}`}
                    className="glass-default border-white/10"
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between gap-6 flex-wrap">
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              booking.booking_status === 'confirmed'
                                ? 'bg-green-500/20 text-green-300'
                                : booking.booking_status === 'pending'
                                ? 'bg-yellow-500/20 text-yellow-300'
                                : booking.booking_status === 'rejected'
                                ? 'bg-red-500/20 text-red-300'
                                : 'bg-blue-500/20 text-blue-300'
                            }`}>
                              {booking.booking_status}
                            </span>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              booking.payment_status === 'paid'
                                ? 'bg-green-500/20 text-green-300'
                                : 'bg-yellow-500/20 text-yellow-300'
                            }`}>
                              Payment: {booking.payment_status}
                            </span>
                          </div>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-white/60">Event Date</p>
                              <p className="font-medium">{new Date(booking.event_date).toLocaleDateString()}</p>
                            </div>
                            <div>
                              <p className="text-white/60">Duration</p>
                              <p className="font-medium">{booking.event_duration}h</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <p className="text-white/60">Booking ID</p>
                              <p className="font-mono text-xs">{booking.booking_id}</p>
                            </div>
                            <div>
                              <p className="text-white/60">Type</p>
                              <p className="font-medium">{booking.is_local ? 'Local' : 'Travel'}</p>
                            </div>
                          </div>
                        </div>

                        {booking.booking_status === 'pending' && (
                          <div className="flex gap-2">
                            <Button
                              data-testid={`confirm-booking-${booking.booking_id}`}
                              onClick={() => handleBookingStatus(booking.booking_id, 'confirmed')}
                              size="sm"
                              className="bg-green-600 hover:bg-green-500"
                            >
                              <CheckCircle size={16} className="mr-1" />
                              Confirm
                            </Button>
                            <Button
                              data-testid={`reject-booking-${booking.booking_id}`}
                              onClick={() => handleBookingStatus(booking.booking_id, 'rejected')}
                              size="sm"
                              variant="outline"
                              className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            >
                              <XCircle size={16} className="mr-1" />
                              Reject
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages" className="space-y-4">
            {conversations.length === 0 ? (
              <Card className="glass-default border-white/10">
                <CardContent className="p-12 text-center">
                  <Inbox size={48} className="mx-auto mb-4 text-white/40" />
                  <p className="text-xl text-white/60">No booking chats yet</p>
                  <p className="text-white/40 mt-2">Every "Book Through Us" conversation appears here — you can read, reply and manage the booking</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {conversations.map((conv) => (
                  <Card
                    key={conv.booking_id}
                    data-testid={`admin-chat-${conv.booking_id}`}
                    className="glass-default border-white/10 cursor-pointer hover:bg-white/5 transition-colors"
                    onClick={() => {
                      setSelectedBooking({ booking_id: conv.booking_id, event_date: conv.event_date });
                      apiClient.post(`/admin/booking-chats/${conv.booking_id}/read`).catch(() => {});
                      setConversations(prev => prev.map(c => c.booking_id === conv.booking_id ? { ...c, team_unread: 0 } : c));
                    }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                            <MessageCircle className="text-violet-400" size={20} />
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">
                              {conv.client_name} ↔ {conv.artist_name}
                            </p>
                            {conv.last_message && (
                              <p className="text-sm text-white/60 truncate">
                                {conv.last_message.sender_name}: {conv.last_message.content}
                              </p>
                            )}
                            <p className="text-xs text-white/40">
                              {conv.message_count} messages · {new Date(conv.event_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {(conv.team_unread || 0) > 0 && (
                            <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center" data-testid={`chat-unread-${conv.booking_id}`}>
                              {conv.team_unread}
                            </span>
                          )}
                        <span className={`px-2 py-1 rounded-full text-xs flex-shrink-0 ${
                          conv.booking_status === 'inquiry'
                            ? 'bg-sky-500/20 text-sky-300'
                            : conv.booking_status === 'confirmed'
                            ? 'bg-green-500/20 text-green-300'
                            : 'bg-yellow-500/20 text-yellow-300'
                        }`}>
                          {conv.booking_status || 'inquiry'}
                        </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Chat Window */}
      {selectedBooking && (
        <ChatWindow
          booking={selectedBooking}
          onClose={() => setSelectedBooking(null)}
        />
      )}
    </AdminLayout>
  );
}
